import { useContext, useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import AuthContext from "@/context/AuthContextInstance";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Login from "./pages/Login";
import StaffPortal from "./pages/StaffPortal";
import AdminPortal from "./pages/AdminPortal";
import VehicleRegistration from "./pages/VehicleRegistration";
import AdminDashboard from "./pages/AdminDashboard";
import NotFound from "./pages/NotFound";
import RegistrationForm from "./components/RegistrationForm";
import ProtectedRoute from "./components/ProtectedRoute";
import Unauthorized from "./pages/Unauthorized";
import VehicleRegistrationTable from "./components/VehicleRegistrationTable";

const queryClient = new QueryClient();

const AppRoutes = () => {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const userRole = user?.role || "staff";
  const userName = user?.name || "Staff Member";
  const userId = user?.id;

  useEffect(() => {
    if (user) {
      navigate(user.role === "admin" ? "/admin" : "/staff", { replace: true });
    }
  }, [user, navigate]);

  return loading ? (
    <div className="flex items-center justify-center h-screen text-lg">
      Checking session...
    </div>
  ) : (
    <>
      {/* {console.log("USER:", user)} */}
      <Routes>
        <Route
          path="/"
          element={
            user ? (
              <Navigate
                to={user.role === "admin" ? "/admin" : "/staff"}
                replace
              />
            ) : (
              <Login />
            )
          }
        />
        <Route
          element={<ProtectedRoute allowedRoles={["admin"]} user={user} />}
        >
          <Route
            path="/admin"
            element={
              <AdminPortal
                userRole={userRole}
                userName={userName}
                userId={userId}
              />
            }
          />
          <Route
            path="/dashboard"
            element={
              <AdminDashboard
                userRole={userRole}
                userName={userName}
                userId={userId}
              />
            }
          />
          <Route
            path="/registration-form"
            element={
              <RegistrationForm userRole={userRole} userName={userName} />
            }
          />
        </Route>
        <Route
          element={<ProtectedRoute allowedRoles={["staff"]} user={user} />}
        >
          <Route
            path="/staff"
            element={
              <StaffPortal
                userRole={userRole}
                userName={userName}
                userId={userId}
              />
            }
          />
        </Route>
        <Route
          element={
            <ProtectedRoute allowedRoles={["admin", "staff"]} user={user} />
          }
        >
          <Route
            path="/registration"
            element={
              <VehicleRegistration
                userRole={userRole}
                userName={userName}
                userId={userId}
              />
            }
          />
          <Route
            path="/registration-table"
            element={
              <VehicleRegistrationTable
                userRole={userRole}
                userName={userName}
                userId={userId}
              />
            }
          />
        </Route>
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AppRoutes />
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
